import type { FileItem } from '../file-browser/types';
import type { FlightLinkCandidate, FlightLinkOrganization } from '../../types/api';

/**
 * Pure rules for the "Link to flight" action (#1694). Unit-tested in
 * `__tests__/flightLinkModel.test.ts`; the dialog and the menu only call them.
 */

/** Whether a file name is an ArduPilot `.bin` log, the one kind a link names. */
export function isBinLog(name: string): boolean {
  return name.toLowerCase().endsWith('.bin');
}

/**
 * Whether the menu offers "Link to flight..." for this entry.
 *
 * Any `.bin` file with a registry row the caller can see, own or shared: any
 * signed-in user who can see a log and its flight may confirm they are the
 * same flight (Jakob, 2026-11-02). The rest is the server's to refuse, and the
 * dialog shows its answer (a flight of another org, a link that would widen a
 * share).
 */
export function canLinkToFlight(entry: Pick<FileItem, 'object_id' | 'isFolder' | 'name'>): boolean {
  return !entry.isFolder && !!entry.object_id && isBinLog(entry.name);
}

/** A `.bin` that has just been uploaded, to ask about. */
export interface UploadedBin {
  objectId: string;
  name: string;
}

/**
 * The `.bin` logs a batch upload stored with a registry row, in upload order.
 *
 * Only these get the "is this the same flight?" prompt: a failed upload has
 * nothing to link, and one whose registration soft-failed has no id (R-9).
 */
export function uploadedBins(
  results: PromiseSettledResult<{ object_id?: string | null }>[],
  names: string[]
): UploadedBin[] {
  const bins: UploadedBin[] = [];
  results.forEach((result, index) => {
    const name = names[index] ?? '';
    if (result.status !== 'fulfilled' || !isBinLog(name)) return;
    const objectId = result.value?.object_id;
    if (objectId) bins.push({ objectId, name });
  });
  return bins;
}

/** The flight the upload prompt asks about: the nearest proposed one, or none. */
export function promptCandidate(candidates: FlightLinkCandidate[]): FlightLinkCandidate | null {
  return candidates.find((c) => c.proposed) ?? null;
}

/** The organizations' names, joined for one line ("Talentis, XER Technologies"). */
export function organizationList(orgs: FlightLinkOrganization[], fallback: string): string {
  return orgs.map((o) => o.organization_name || fallback).join(', ');
}

/** The candidates the dialog lists first: proposed, then the rest, in server order. */
export function splitCandidates(candidates: FlightLinkCandidate[]): {
  proposed: FlightLinkCandidate[];
  others: FlightLinkCandidate[];
} {
  return {
    proposed: candidates.filter((c) => c.proposed),
    others: candidates.filter((c) => !c.proposed),
  };
}

/** `YYYY-MM-DD` of the day the log's name spells, for the day picker's default. */
export function logDay(logStart: string | null | undefined): string {
  return logStart ? logStart.slice(0, 10) : '';
}
